import React, { useState, useEffect, useRef } from 'react';
import { ProfilePhoto } from '../types';
import {
  StepContainer,
  StepTitle,
  StepDescription,
  PhotosContainer,
  PhotoGrid,
  PhotoSlot,
  PhotoUpload,
  PhotoPreview,
  PhotoActions,
  PhotoOrder,
  PhotoValidation,
  UploadButton,
  RemoveButton,
  ReorderButton,
  DragHandle,
  PhotoTips,
  TipItem,
  ValidationStatus,
  ErrorMessage,
  SuccessMessage
} from '../../../styles/Profile/ProfileSetup.styles';

interface PhotosStepProps {
  data: Partial<{ photos: ProfilePhoto[] }>;
  onChange: (data: { photos: ProfilePhoto[] }, isValid: boolean) => void;
  onNext: () => void;
  onBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const MAX_PHOTOS = 6;
const MIN_PHOTOS = 1;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const PhotosStep: React.FC<PhotosStepProps> = ({
  data,
  onChange,
  onNext,
  onBack,
  isFirstStep,
  isLastStep
}) => {
  const [photos, setPhotos] = useState<ProfilePhoto[]>(data.photos || []);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validatePhotos = (): boolean => {
    return photos.length >= MIN_PHOTOS && photos.every(photo => photo.isValidated);
  };

  const handleFileUpload = async (files: FileList) => {
    setUploading(true);
    const newPhotos: ProfilePhoto[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`File ${file.name} is not a supported image format`);
        continue;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        alert(`File ${file.name} is too large. Maximum size is 10MB`);
        continue;
      }

      if (photos.length + newPhotos.length >= MAX_PHOTOS) {
        alert(`You can only upload up to ${MAX_PHOTOS} photos`);
        break;
      }

      try {
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        
        const newPhoto: ProfilePhoto = {
          id: `temp_${Date.now()}_${i}`,
          url: previewUrl,
          order: photos.length + newPhotos.length,
          isMain: photos.length + newPhotos.length === 0,
          uploadedAt: new Date(),
          isValidated: false,
          validationStatus: 'pending'
        };

        newPhotos.push(newPhoto);
      } catch (error) {
        console.error('Error processing file:', error);
        alert(`Error processing file ${file.name}`);
      }
    }

    if (newPhotos.length > 0) {
      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);
      onChange({ photos: updatedPhotos }, validatePhotos());
    }

    setUploading(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (e.dataTransfer.files) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removePhoto = (photoId: string) => {
    const updatedPhotos = photos.filter(photo => photo.id !== photoId);
    // Reorder remaining photos
    const reorderedPhotos = updatedPhotos.map((photo, index) => ({
      ...photo,
      order: index,
      isMain: index === 0
    }));
    
    setPhotos(reorderedPhotos);
    onChange({ photos: reorderedPhotos }, validatePhotos());
  };

  const setMainPhoto = (photoId: string) => {
    const updatedPhotos = photos.map(photo => ({
      ...photo,
      isMain: photo.id === photoId
    }));
    
    setPhotos(updatedPhotos);
    onChange({ photos: updatedPhotos }, validatePhotos());
  };

  const reorderPhotos = (fromIndex: number, toIndex: number) => {
    const updatedPhotos = [...photos];
    const [movedPhoto] = updatedPhotos.splice(fromIndex, 1);
    updatedPhotos.splice(toIndex, 0, movedPhoto);
    
    // Update order and main photo
    const reorderedPhotos = updatedPhotos.map((photo, index) => ({
      ...photo,
      order: index,
      isMain: index === 0
    }));
    
    setPhotos(reorderedPhotos);
    onChange({ photos: reorderedPhotos }, validatePhotos());
  };

  const simulateValidation = (photoId: string) => {
    // Simulate photo validation process
    setTimeout(() => {
      setPhotos(prev => prev.map(photo => 
        photo.id === photoId 
          ? { 
              ...photo, 
              isValidated: true, 
              validationStatus: 'approved' as const 
            }
          : photo
      ));
    }, 2000);
  };

  useEffect(() => {
    // Simulate validation for new photos
    photos.forEach(photo => {
      if (!photo.isValidated && photo.validationStatus === 'pending') {
        simulateValidation(photo.id);
      }
    });
  }, [photos]);

  const renderPhotoSlot = (index: number) => {
    const photo = photos.find(p => p.order === index);
    
    if (photo) {
      return (
        <PhotoSlot key={photo.id} $isMain={photo.isMain}>
          <PhotoPreview>
            <img src={photo.url} alt={`Profile photo ${index + 1}`} />
            {photo.isMain && <div className="main-badge">Main Photo</div>}
          </PhotoPreview>
          
          <PhotoActions>
            <PhotoOrder>{index + 1}</PhotoOrder>
            
            {!photo.isMain && (
              <button onClick={() => setMainPhoto(photo.id)}>
                Set as Main
              </button>
            )}
            
            <RemoveButton onClick={() => removePhoto(photo.id)}>
              Remove
            </RemoveButton>
          </PhotoActions>
          
          <PhotoValidation>
            {photo.validationStatus === 'pending' && (
              <ValidationStatus $status="pending">
                Validating...
              </ValidationStatus>
            )}
            {photo.validationStatus === 'approved' && (
              <ValidationStatus $status="approved">
                ✓ Approved
              </ValidationStatus>
            )}
            {photo.validationStatus === 'rejected' && (
              <ValidationStatus $status="rejected">
                ✗ Rejected: {photo.rejectionReason}
              </ValidationStatus>
            )}
          </PhotoValidation>
        </PhotoSlot>
      );
    }

    return (
      <PhotoSlot key={`empty-${index}`} $isEmpty>
        <PhotoUpload
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          isDragOver={dragOver}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="upload-icon">📷</div>
          <div className="upload-text">
            {dragOver ? 'Drop photo here' : 'Click to upload'}
          </div>
        </PhotoUpload>
      </PhotoSlot>
    );
  };

  return (
    <StepContainer>
      <StepTitle>Add your photos</StepTitle>
      <StepDescription>
        Upload up to 6 photos to showcase yourself. Your first photo will be your main profile picture.
      </StepDescription>

      <PhotosContainer>
        <PhotoGrid>
          {Array.from({ length: MAX_PHOTOS }, (_, index) => renderPhotoSlot(index))}
        </PhotoGrid>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {photos.length < MAX_PHOTOS && (
          <UploadButton
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Add More Photos'}
          </UploadButton>
        )}
      </PhotosContainer>

      <PhotoTips>
        <h4>Photo Tips:</h4>
        <TipItem>✓ Use clear, well-lit photos</TipItem>
        <TipItem>✓ Show your face clearly in the first photo</TipItem>
        <TipItem>✓ Include photos that show your personality</TipItem>
        <TipItem>✓ Avoid group photos or photos with other people</TipItem>
        <TipItem>✓ Use high-quality images</TipItem>
      </PhotoTips>

      {photos.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          {photos.length >= MIN_PHOTOS ? (
            <SuccessMessage>
              ✓ Great! You have {photos.length} photo{photos.length !== 1 ? 's' : ''} uploaded.
            </SuccessMessage>
          ) : (
            <ErrorMessage>
              Please upload at least {MIN_PHOTOS} photo to continue.
            </ErrorMessage>
          )}
        </div>
      )}
    </StepContainer>
  );
};
